// react
import {
  useEffect,
  useState,
  Dispatch,
  SetStateAction,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
// node-module
import axios, { AxiosResponse } from "axios";
import get from "lodash/get";
import JSZip from "jszip";
// mui
import {
  DialogActions,
  Button,
  DialogContentText,
  TextField,
  FormControl,
  useTheme,
  styled,
  Autocomplete,
  FormLabel,
  Box,
  Popover,
  IconButton,
  PopoverOrigin,
  Typography,
  RadioGroup,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import { LoadingButton } from "@mui/lab";
// src
import { Common } from "../../../common/src";
import { config } from "../../config/config";
import {
  coordinateSystem as coordinateSystemData,
  verticalShift,
} from "../../config/SelectListData";
import { ColorResult, SketchPicker } from "react-color";
import CoordinateSystemJobSite from "./parts/CoordinateSystemJobSite";
import {
  fetchFileGroupWare,
  fetchJobSiteCoordinateSystem,
  fetchJobsiteList,
} from "../../api/project";
import {
  ECoordinateOptions,
  EFileImportFrom,
  ProjectViDoc,
} from "../../../common/src/modules";
import Spacer from "../Spacer";
import IOSSwitch from "../IOSSwitch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleQuestion } from "@fortawesome/free-solid-svg-icons";
import Radio from "@mui/material/Radio";
import { fetchUserSetting } from "../../api/user";
import { formatFileSize } from "../../utils/file";
import {
  dashboard,
  dashboardDark,
  design3D,
  design3DDark,
} from "../../../images/icons/icon";
import ImportFileGroupWare from "./ImportFileGroupware";
import "../../../css/formTheme.css";

type UploadProps = {
  setErrorSnackbar: Dispatch<SetStateAction<boolean>>;
  setErrorMsg: Dispatch<SetStateAction<string>>;
  setOpenWarning: Dispatch<SetStateAction<boolean>>;
  setMsgWarning: Dispatch<SetStateAction<string>>;
  setProgressDialog: Dispatch<SetStateAction<boolean>>;
  setUploadResult: Dispatch<SetStateAction<boolean>>;
  setUpLoadingPercent: Dispatch<SetStateAction<number>>;
  setOpenDialog: Dispatch<SetStateAction<boolean>>;
  openDialog: boolean;
  selectType: number;
};

interface FileState {
  file: File | null;
  name: string;
  size: string;
  selected: boolean;
  loading: boolean;
}

export interface FileInfoSelectedFromGroupware {
  name: string;
  size: number;
  bucketId: string;
  nodeId: string;
  key: string;
}

const initialFileState: FileState = {
  file: null,
  name: "",
  size: "",
  selected: false,
  loading: false,
};

const initialFileInfoState: FileInfoSelectedFromGroupware = {
  name: "",
  size: undefined,
  bucketId: "",
  nodeId: "",
  key: "",
};

// SC_QUICK-2195 (QU3DW-1381): "Import from other service" icon (Dashboard /
// Design3D) with a tooltip. Extracted so the tooltip behavior can be tested
// without mounting the full dialog. The disabled icon is wrapped in a <span>
// (MUI's documented pattern for tooltips on disabled elements) so `pointerEvents:
// none` on the icon itself does not also block the hover that opens the tooltip
// (AC-9: the tooltip must still show while the icon is disabled).
export interface ImportSourceIconProps {
  iconSrc: string;
  tooltipText: string;
  disabled: boolean;
  marginLeft?: number;
  marginRight: number;
  onClick: () => void;
  ariaLabel: string;
}

const DARK_MODE_ELEVATION_8_OVERLAY =
  "linear-gradient(rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.12))";

const DARK_MODE_ELEVATION_8_FLAT = "rgb(46, 46, 46)";

export const ImportSourceIcon = ({
  iconSrc,
  tooltipText,
  disabled,
  marginLeft,
  marginRight,
  onClick,
  ariaLabel,
}: ImportSourceIconProps) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const hintBubbleTooltipProps = {
    tooltip: {
      sx: {
        backgroundColor: theme.palette.background.paper,
        backgroundImage: isDarkMode ? DARK_MODE_ELEVATION_8_OVERLAY : "none",
        color: theme.palette.text.primary,
        fontSize: "1rem",
        fontWeight: 400,
        lineHeight: 1.5,
        padding: "16px",
        maxWidth: 400,
        borderRadius: "4px",
        boxShadow:
          "0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)",
      },
    },

    arrow: {
      sx: {
        color: isDarkMode
          ? DARK_MODE_ELEVATION_8_FLAT
          : theme.palette.background.paper,
      },
    },
  };

  return (
    <Tooltip
      title={tooltipText}
      placement="top"
      arrow
      disableInteractive
      componentsProps={hintBubbleTooltipProps}
    >
      <span
        style={{ display: "inline-block" }}
        data-testid="import-source-icon"
      >
        <div
          style={{
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.5 : 1,
            marginLeft,
            marginRight,
            width: 40,
            height: 40,
            marginTop: 12,
            backgroundImage: `url(${iconSrc})`,
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            transform: "translateZ(0)",
            WebkitTransform: "translateZ(0)",
            pointerEvents: disabled ? "none" : "auto",
          }}
          onClick={() => {
            if (!disabled) onClick();
          }}
          role="button"
          tabIndex={0}
          aria-label={ariaLabel}
        />
      </span>
    </Tooltip>
  );
};

const UploadARFileDialog = ({
  setErrorSnackbar,
  setErrorMsg,
  setOpenWarning,
  setMsgWarning,
  setProgressDialog,
  setUploadResult,
  setUpLoadingPercent,
  setOpenDialog,
  openDialog,
  selectType,
}: UploadProps) => {
  const { t, i18n } = useTranslation();
  // Loding flag
  const [whileLoading, setWhileLoading] = useState(false);
  const [file, setFile] = useState<File>();
  const [projectName, setProjectName] = useState<string>("");
  const [jobsiteId, setJobsiteId] = useState<string>("");
  const [timeStamp, setTimeStamp] = useState<number>(0);
  const [useArmarker, setUseArmarker] = useState<string>("false");
  const [jobsiteList, setJobsiteList] = useState<Common.LandlogJobSite[]>([]);
  const [fileDashboardInfo, setFileDashboardInfo] = useState<any>([]);
  const [fileDesignInfo, setFileDesignInfo] = useState<any>([]);
  const [fileInfo, setFileInfo] = useState<any>([]);
  const [loadingFileGroupware, setLoadingFileGroupware] = useState(false);
  const [fileNameDownload, setFileNameDownload] = useState<string>("");
  const [fileSizeDownload, setFileSizeDownload] = useState<number>();
  const [bucketId, setBucketId] = useState<string>("");
  const [nodeId, setNodeId] = useState<string>("");
  const [keyFile, setKeyFile] = useState<string>("");

  // Importボタン制御
  const [uploadCheck, setUploadCheck] = useState(true);
  const [validationCheck, setValidationCheck] = useState(false);

  const [pickerColor, setPickerColor] = useState("#50E3C2");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedJobsiteCoordinateSystem, setSelectedJobsiteCoordinateSystem] =
    useState<ProjectViDoc | null>(null);

  const [selectedDestData, setSelectedData] = useState<any[]>([
    { id: "horizontal", value: "", label: "" },
    { id: "vertical", value: "", label: "" },
  ]);
  const [coordinateSystemLoading, setCoordinateSystemLoading] =
    useState<boolean>(false);
  const [selectedCoordinateOption, setSelectedCoordinateOption] =
    useState<ECoordinateOptions | null>(null);
  const [fileInfoFrom, setFileInfoFrom] = useState<EFileImportFrom | null>(
    null,
  );
  const [isCoordinateSetting, setIsCoordinateSetting] = useState<boolean>(true);
  const [selectedOption, setSelectedOption] = useState<string>("");
  const [openImportDialog, setOpenImportDialog] = useState<boolean>(false);
  const [fileData, setFileData] = useState();

  const [fileStates, setFileStates] = useState<{
    design: FileState;
    las: FileState;
    tif: FileState;
    xml: FileState;
    json: FileState;
  }>({
    design: { ...initialFileState },
    las: { ...initialFileState },
    tif: { ...initialFileState },
    xml: { ...initialFileState },
    json: { ...initialFileState },
  });

  const [fileInfoSelected, setFileInfoSelected] =
    useState<FileInfoSelectedFromGroupware>({ ...initialFileInfoState });

  useEffect(() => {
    const handler = () => {
      if (!openDialog) document.body.style.cssText = "";
    };

    window.addEventListener("focus", handler);
    return () => window.removeEventListener("focus", handler);
  }, [openDialog]);

  useEffect(() => {
    // jobsiteデータ取得
    const fetchJobsites = async () => {
      const res = await fetchJobsiteList();
      setJobsiteList(res.sites);
    };

    fetchJobsites();
  }, []);

  useEffect(() => {
    if (!jobsiteId || jobsiteId === "") return;
    if (selectType === 1 || selectType === 2) {
      setFileDashboardInfo([]);
      setFileDesignInfo([]);
      setFileInfo([]);

      const controller = new AbortController();

      const fetchFileInfo = async () => {
        try {
          setLoadingFileGroupware(true);
          const res = await fetchFileGroupWare(
            selectType,
            jobsiteId,
            i18n.language,
            controller.signal,
          );
          setFileDashboardInfo(res.filesDashboard);
          setFileDesignInfo(res.filesDesign);
        } catch (err: any) {
          if (err.name === "CanceledError") {
            console.log("Cancel");
          } else {
            console.error(err);
          }
        } finally {
          setLoadingFileGroupware(false);
        }
      };

      fetchFileInfo();

      return () => {
        controller.abort();
      };
    }
  }, [selectType, jobsiteId]);

  function isXMLIdentifier(identifier?: string): boolean {
    if (identifier && identifier !== "") {
      const parts = identifier.split(".");
      return parts[parts.length - 1] === "xml";
    } else {
      return false;
    }
  }

  const canUpload = useMemo(() => {
    const hasFile =
      fileStates.design.selected ||
      fileStates.las.selected ||
      (fileStates.tif.selected && fileStates.xml.selected) ||
      fileStates.json.selected;

    const hasCoordinate =
      (selectedCoordinateOption === ECoordinateOptions.PUBLIC_COORDINATE &&
        selectedDestData.some(
          (i) => i.id === "horizontal" && i.value?.trim() !== "",
        ) &&
        selectedDestData.some(
          (i) => i.id === "vertical" && i.value?.trim() !== "",
        )) ||
      (selectedCoordinateOption === ECoordinateOptions.DASHBOARD &&
        !!selectedJobsiteCoordinateSystem);

    return (
      hasFile && jobsiteId && projectName && !validationCheck && hasCoordinate
    );
  }, [
    fileStates,
    jobsiteId,
    projectName,
    validationCheck,
    selectedDestData,
    selectedCoordinateOption,
    selectedJobsiteCoordinateSystem,
  ]);

  useEffect(() => {
    setUploadCheck(!canUpload);
  }, [canUpload]);

  useEffect(() => {
    if (openDialog) {
      handleOpen();
      document.body.style.cssText = `position: fixed; top: -${window.scrollY}px; overflow-y: scroll; width: 100%;`;
    }
  }, [openDialog]);

  const jobsite = useMemo(() => {
    return jobsiteList.map((data) => ({
      value: data.id,
      label: data.name,
    }));
  }, [jobsiteList]);

  useEffect(() => {
    setFileStates({
      design: initialFileState,
      las: initialFileState,
      tif: initialFileState,
      xml: initialFileState,
      json: initialFileState,
    });

    setFileInfoSelected({ ...initialFileInfoState });
  }, [selectType]);

  useEffect(() => {
    const getJobSiteInfo = async (controller: AbortController) => {
      try {
        setSelectedJobsiteCoordinateSystem(null);
        if (jobsiteId) {
          setCoordinateSystemLoading(true);
          setSelectedJobsiteCoordinateSystem(null);
          const coordinateSystem = await fetchJobSiteCoordinateSystem(
            controller,
            {
              jobsiteId,
            },
          );
          setSelectedJobsiteCoordinateSystem(coordinateSystem || null);
          if (!coordinateSystem) {
            setIsCoordinateSetting(false);
            setSelectedCoordinateOption(ECoordinateOptions.PUBLIC_COORDINATE);
          }
        }
      } catch (e: any) {
        if (e?.message !== "canceled") {
          setSelectedJobsiteCoordinateSystem(null);
          setIsCoordinateSetting(false);
          setSelectedCoordinateOption(ECoordinateOptions.PUBLIC_COORDINATE);
        }
        console.log(e);
      } finally {
        setCoordinateSystemLoading(false);
      }
    };
    const controller = new AbortController();
    getJobSiteInfo(controller);

    return () => {
      controller.abort();
    };
  }, [jobsiteId]);

  const handleOpen = async () => {
    try {
      const res = await fetchUserSetting();
      setIsCoordinateSetting(res.coordinateSetting);
    } catch (e) {
      console.error(e);
    }
  };

  const handleClose = () => {
    setOpenDialog(false);
    setFileStates({
      design: { ...initialFileState },
      las: { ...initialFileState },
      tif: { ...initialFileState },
      xml: { ...initialFileState },
      json: { ...initialFileState },
    });
    setFileInfoSelected({ ...initialFileInfoState });
    setJobsiteId("");
    setProjectName("");

    const scrollY = document.body.style.top;
    document.body.style.cssText = "";
    window.scrollTo(0, parseInt(scrollY || "0", 10) * -1);
  };

  const clearUploadFileDisplay = () => {
    setFileStates({
      design: { ...initialFileState },
      las: { ...initialFileState },
      tif: { ...initialFileState },
      xml: { ...initialFileState },
      json: { ...initialFileState },
    });
    setFileInfoSelected({ ...initialFileInfoState });
  };

  const getValueSelect = useCallback(
    (type: string) => {
      const value = selectedDestData?.find((x) => x.id === type)?.value;
      const list = type === "horizontal" ? coordinateSystemData : verticalShift;
      return list.find((x) => x.value === value) || null;
    },
    [selectedDestData],
  );

  const uploadProjectFile = async () => {
    const isHeatmap = selectType === 3;

    setProgressDialog(true);
    setOpenDialog(false);
    setFileStates({
      design: { ...initialFileState },
      las: { ...initialFileState },
      tif: { ...initialFileState },
      xml: { ...initialFileState },
      json: { ...initialFileState },
    });
    setFileInfoSelected({ ...initialFileInfoState });
    setJobsiteId("");
    setWhileLoading(true);

    try {
      const designFile = fileStates.design.file;
      const lasFile = fileStates.las.file;
      const tifFile = fileStates.tif.file;
      const xmlFile = fileStates.xml.file;
      const geoJsonFile = fileStates.json.file;

      const hasValidFile = isHeatmap
        ? tifFile && xmlFile
        : !!designFile || !!lasFile || !!geoJsonFile;

      const hasValidFileGW = !(
        fileInfoSelected.name !== "" && !fileInfoSelected.size
      );

      if (!hasValidFile && !hasValidFileGW) {
        setErrorSnackbar(true);
        setErrorMsg(t("FILE_NOT_EXISTS"));
        setWhileLoading(false);
        setProgressDialog(false);
        return;
      }

      const data = new FormData();

      if (isHeatmap && tifFile && xmlFile) {
        const zip = new JSZip();
        zip.file(tifFile.name, tifFile);
        zip.file(xmlFile.name, xmlFile);
        const zipBlob = await zip.generateAsync({ type: "blob" });
        data.append("uploadFile", zipBlob, "heatmap_data.zip");
        data.append("uploadFileName", "heatmap_data.zip");
      } else if (designFile) {
        data.append("uploadFile", designFile);
        data.append("uploadFileName", designFile.name);
      } else if (lasFile) {
        data.append("uploadFile", lasFile);
        data.append("uploadFileName", lasFile.name);
      } else if (geoJsonFile) {
        data.append("uploadFile", geoJsonFile);
        data.append("uploadFileName", geoJsonFile.name);
      }

      if (fileInfoSelected.name !== "" && fileInfoSelected.key) {
        data.append("bucketId", fileInfoSelected.bucketId);
        data.append("nodeId", fileInfoSelected.nodeId);
        data.append("key", fileInfoSelected.key);
        data.append("name", fileInfoSelected.name);
        data.append("uploadFileName", fileInfoSelected.name);
      }

      data.append("projectName", projectName);
      data.append("jobsiteId", jobsiteId);
      data.append("ARColor", pickerColor);
      data.append("coordinate_type", selectedCoordinateOption);

      if (selectedCoordinateOption === ECoordinateOptions.PUBLIC_COORDINATE) {
        data.append(
          "vertical",
          selectedDestData?.find((x) => x.id === "vertical")?.value,
        );
        data.append(
          "horizontal",
          selectedDestData?.find((x) => x.id === "horizontal")?.value,
        );
      } else if (
        selectedCoordinateOption === ECoordinateOptions.DASHBOARD &&
        !!selectedJobsiteCoordinateSystem
      ) {
        let keys = ["vertical", "horizontal"];
        if (selectedJobsiteCoordinateSystem?.originEasting) {
          keys = [
            "originEasting",
            "originNorthing",
            "originLatitude",
            "originLongitude",
            "scaleFactor",
            "verticalOffset",
            "inclineX",
            "inclineY",
            "rotation",
          ];
        }
        keys.forEach((key) => {
          const val = get(selectedJobsiteCoordinateSystem, key, null);
          data.append(key, val);
        });
      }
      const timeStamp = 0;
      const useArmarker = "false";

      if (timeStamp !== 0) {
        const d = new Date(timeStamp);
        data.append("timeStamp", d.toISOString());
      }

      const timezoneOffset = new Date().getTimezoneOffset() * -1;
      data.append("timezone", `${timezoneOffset}`);
      data.append("useArmarker", useArmarker);

      await axios
        .post(`${config.serverHost}/api/v1/project/ar`, data, {
          withCredentials: true,
          onUploadProgress: (progressEvent) => {
            const percent = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            setUpLoadingPercent(percent);
          },
        })
        .then(() => {
          setUploadResult(true);
          setProgressDialog(false);
        })
        .catch((err) => {
          if (err.response?.status === 504) {
            setOpenWarning(true);
            setMsgWarning(t("SERVER_TIME_OUT"));
          } else {
            setErrorSnackbar(true);
            setErrorMsg(t("UPLOAD_FAILED"));
          }
          setWhileLoading(false);
          setProgressDialog(false);
        });
    } catch (err) {
      setErrorSnackbar(true);
      setWhileLoading(false);
      setProgressDialog(false);
      setErrorMsg(t("UPLOAD_FAILED"));
    }
  };

  const onChangeHandle = (e: string) => {
    // 正規表現
    const regexHead = /^-/g;
    const regexEnd = /\s$/g;
    const regexContent = /[\\/\r\n]/g;

    setProjectName(e);
    // 入力チェック
    if (regexHead.test(e) || regexEnd.test(e) || regexContent.test(e))
      setValidationCheck(true);
    else setValidationCheck(false);
  };

  const theme = useTheme();

  const customColor = {
    color: theme.palette.base.contrast,
  };

  const customBackgroundStyles = {
    background: theme.palette.base.modal,
  };

  const customMainColor = {
    background: theme.palette.primary.main,
    boxShadow: "none",
  };

  // Alpha値を16進数に変換する処理
  const decimalToHex = (alpha: number) =>
    alpha === 0
      ? "00"
      : Math.round(255 * alpha)
          .toString(16)
          .padStart(2, "0");

  const handleChangeComplete = (newColor: ColorResult) => {
    const alpha = newColor.rgb.a;
    const hexCode = `${newColor.hex}${decimalToHex(alpha || 0)}`;
    setPickerColor(hexCode);
  };

  const CustomCanselButton = styled(Button)({
    background: theme.palette.base.modal,
    color: theme.palette.base.contrast,
    boxShadow: "none",
    "&:hover": {
      background: theme.palette.base.modal,
      color: theme.palette.base.contrast,
      boxShadow: "none",
    },
  });

  const Icon_Design3D =
    theme.palette.mode === "light" ? design3D : design3DDark;
  const Icon_Dashboard =
    theme.palette.mode === "light" ? dashboard : dashboardDark;

  const handlePickerOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setPickerOpen(true);
  };

  const handlePickerClose = () => {
    setPickerOpen(false);
  };

  const buttonStyle = {
    backgroundColor: "transparent",
    boxShadow: "none",
    "&:hover": {
      background: "none",
      boxShadow: "none",
    },
  };

  const circleStyle = {
    backgroundColor: pickerColor,
    borderRadius: "50%",
    width: 35,
    height: 35,
  };

  const borderStyle = {
    border: "2px solid " + customColor.color,
    borderRadius: "50%",
    width: "100%",
    height: "100%",
  };

  const selectedJobsiteOption = useMemo(() => {
    return jobsite.find((option) => option.value === jobsiteId) || null;
  }, [jobsiteId, jobsite]);

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "design" | "las" | "tif" | "xml" | "json",
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    updateFileState(file, type);
  };

  const updateFileState = (
    file: File,
    type: "design" | "las" | "tif" | "xml" | "json",
  ) => {
    setFileInfoSelected({ ...initialFileInfoState });
    setFileStates((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        file,
        selected: true,
        name: file.name,
        size: formatFileSize(file.size),
        loading: false,
      },
    }));
  };

  const updateFileStateFromGroupware = (
    fileName: string,
    fileSize: number,
    type: "design" | "las" | "tif" | "xml" | "json",
  ) => {
    setFileStates((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        file: null,
        selected: true,
        name: fileName,
        size: formatFileSize(fileSize),
        loading: false,
      },
    }));
  };

  useEffect(() => {
    if (fileInfoSelected.name !== "" && selectType === 1)
      updateFileStateFromGroupware(
        fileInfoSelected.name,
        fileInfoSelected.size,
        "design",
      );
    else if (fileInfoSelected.name !== "" && selectType === 2)
      updateFileStateFromGroupware(
        fileInfoSelected.name,
        fileInfoSelected.size,
        "las",
      );
  }, [fileInfoSelected]);

  type CustomPopoverProps = {
    anchorOrigin: PopoverOrigin;
    transformOrigin: PopoverOrigin;
    content: string;
  };
  const CustomPopover = ({
    anchorOrigin,
    transformOrigin,
    content,
  }: CustomPopoverProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const replaceNewLine = (s: String) => {
      return s.split("\n").map((line) => (
        <span>
          {line}
          <br />
        </span>
      ));
    };

    const open = Boolean(anchorEl);

    return (
      <div>
        <IconButton onClick={handleClick} style={{ padding: 0 }}>
          <FontAwesomeIcon icon={faCircleQuestion} style={customColor} />
        </IconButton>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={anchorOrigin}
          transformOrigin={transformOrigin}
        >
          <Typography sx={{ p: 2 }} style={{ width: 400 }}>
            {content ? replaceNewLine(content) : ""}
          </Typography>
        </Popover>
      </div>
    );
  };

  const handleOpenImport = () => {
    setOpenImportDialog(true);
  };

  const FileUploadSection = ({
    label,
    type,
    accept,
  }: {
    label: string;
    type: "design" | "las" | "tif" | "xml" | "json";
    accept: string;
  }) => {
    const { selected, name, size, loading } = fileStates[type];

    return (
      <>
        <div style={{ display: "flex" }}>
          <DialogContentText style={customColor} className="upload-file7">
            {label}
          </DialogContentText>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-start",
            marginLeft: "30px",
            flex: 1,
          }}
        >
          <div style={{ display: "flex" }}>
            <LoadingButton
              style={{
                ...customMainColor,
                boxShadow: "none",
                marginTop: "10px",
                marginBottom: "15px",
              }}
              size="small"
              variant="contained"
              component="label"
              className={selected ? "upload-file8" : "upload-file6"}
              loading={loading}
            >
              {selected ? t("FILE_CHANGE") : t("FILE_SELECT")}
              <input
                type="file"
                hidden
                accept={accept}
                onChange={(e) => {
                  handleFileChange(e, type);
                }}
              />
            </LoadingButton>
            {(type === "design" || type === "las") && (
              <>
                {(() => {
                  const disableGroupWare = !jobsiteId;
                  return (
                    <>
                      <ImportSourceIcon
                        iconSrc={Icon_Dashboard}
                        tooltipText={t("TOOLTIP_LINK_DASHBOARD")}
                        disabled={disableGroupWare}
                        marginLeft={20}
                        marginRight={20}
                        ariaLabel="Upload from GroupWare"
                        onClick={() => {
                          handleOpenImport();
                          setFileInfoFrom(EFileImportFrom.DASHBOARD);
                        }}
                      />
                      <ImportSourceIcon
                        iconSrc={Icon_Design3D}
                        tooltipText={t("TOOLTIP_LINK_DESIGN3D")}
                        disabled={disableGroupWare}
                        marginRight={20}
                        ariaLabel="Upload from GroupWare"
                        onClick={() => {
                          handleOpenImport();
                          setFileInfoFrom(EFileImportFrom.DESIGN_3D);
                        }}
                      />
                    </>
                  );
                })()}
              </>
            )}
          </div>

          {selected && (
            <DialogContentText
              style={{ flex: 1, ...customColor, minWidth: 0 }}
              className="upload-file7 upload-file10"
            >
              <FolderZipIcon
                style={customColor}
                sx={{ width: "60px", height: "60px", flexShrink: 0 }}
              />
              <div
                id="upload-file11"
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minWidth: 0,
                  overflow: "hidden",
                  justifyContent: "center",
                }}
              >
                <p
                  id="upload-file12"
                  style={{
                    margin: 0,
                    width: "100%",
                  }}
                  className="truncate-single-line"
                >
                  {name}
                </p>
                <p
                  id="upload-file13"
                  style={{ margin: 0, flexShrink: 0 }}
                  className="truncate-single-line"
                >
                  {size}
                </p>
              </div>
            </DialogContentText>
          )}
        </div>
      </>
    );
  };

  let loadingFile =
    fileStates.design.loading ||
    fileStates.las.loading ||
    fileStates.xml.loading ||
    fileStates.tif.loading ||
    fileStates.json.loading;

  useEffect(() => {
    setSelectedCoordinateOption(
      isCoordinateSetting
        ? ECoordinateOptions.DASHBOARD
        : ECoordinateOptions.PUBLIC_COORDINATE,
    );
  }, [isCoordinateSetting]);

  useEffect(() => {
    if (fileDashboardInfo || fileDesignInfo) {
      if (fileInfoFrom === EFileImportFrom.DASHBOARD)
        setFileInfo(fileDashboardInfo);
      else setFileInfo(fileDesignInfo);
    }
  }, [fileInfoFrom, fileDashboardInfo, fileDesignInfo]);

  return (
    <>
      <DialogContentText style={customColor} className="upload-file1">
        {t("PLS_SELECT_JOBSITE")}
      </DialogContentText>
      <FormControl
        variant="outlined"
        className="form-upload-input-container"
        size="small"
      >
        <Autocomplete
          value={selectedJobsiteOption}
          onChange={(event, newValue) => {
            const nextJobsiteId = newValue ? newValue.value : "";
            if (nextJobsiteId !== jobsiteId) {
              clearUploadFileDisplay();
            }
            setJobsiteId(newValue ? newValue.value : null);
          }}
          options={jobsite}
          getOptionLabel={(option) => option.label}
          onInputChange={(event, newInputValue) => {
            // × ボタンで clear されたときの処理
            if (newInputValue === "") {
              setJobsiteId(null); // state を clear する
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              InputLabelProps={{ shrink: true }}
              placeholder={t("PLS_SELECT")}
              InputProps={{
                ...params.InputProps,
                style: {
                  paddingTop: 0,
                  paddingBottom: 0,
                },
              }}
            />
          )}
        />
      </FormControl>

      <CoordinateSystemJobSite
        vidocData={selectedJobsiteCoordinateSystem}
        jobsiteName={selectedJobsiteOption?.label}
        isLoading={coordinateSystemLoading}
      />

      {selectType === 1 ? (
        <FileUploadSection
          label={t("PLS_FILE_PROJECT")}
          type="design"
          accept=".ifc,.xml"
        />
      ) : selectType === 2 ? (
        <FileUploadSection
          label={t("PLS_FILE_PROJECT")}
          type="las"
          accept=".las"
        />
      ) : selectType === 3 ? (
        <>
          <FileUploadSection
            label={t("PLEASE_FILE_TIFF_SELECT")}
            type="tif"
            accept=".tif, .tiff"
          />
          <FileUploadSection
            label={t("PLEASE_FILE_XML_SELECT")}
            type="xml"
            accept=".xml"
          />
        </>
      ) : (
        <FileUploadSection
          label={t("PLEASE_FILE_SELECT")}
          type="json"
          accept=".json"
        />
      )}

      {isXMLIdentifier(fileStates.design?.name) && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            verticalAlign: "middle",
            marginLeft: "30px",
          }}
        >
          <div style={{ margin: "auto 0" }}>
            <p style={{ marginBottom: 0 }}>{t("AR_DATA_COLOR")}</p>
          </div>
          <div>
            <Button
              variant="contained"
              sx={buttonStyle}
              onClick={handlePickerOpen}
            >
              <Box sx={circleStyle}>
                <Box sx={borderStyle} />
              </Box>
            </Button>
          </div>
        </div>
      )}
      <Popover
        open={pickerOpen}
        anchorEl={anchorEl}
        onClose={handlePickerClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "left",
        }}
        PaperProps={{
          style: {
            background: "transparent",
            boxShadow: "none",
          },
        }}
      >
        <div style={{ marginLeft: 40 }}>
          <SketchPicker
            color={pickerColor}
            onChangeComplete={handleChangeComplete}
            disableAlpha={false}
          />
        </div>
      </Popover>
      <DialogContentText style={customColor} id="upload-file14">
        {t("PLS_INPUT_PROJECT_NAME")}
      </DialogContentText>
      {validationCheck ? (
        <TextField
          error
          helperText={t("INVALID_CHARACTERS")}
          autoFocus
          margin="dense"
          hiddenLabel
          placeholder={t("INPUT_PROJECT_NAME")}
          onChange={(e) => onChangeHandle(e.target.value)}
          type="string"
          variant="outlined"
          className="form-upload-input-container"
          InputProps={{ className: "form-upload-input" }}
        />
      ) : (
        <TextField
          autoFocus
          margin="dense"
          hiddenLabel
          placeholder={t("INPUT_PROJECT_NAME")}
          onChange={(e) => onChangeHandle(e.target.value)}
          type="string"
          variant="outlined"
          className="form-upload-input-container"
          InputProps={{ className: "form-upload-input" }}
        />
      )}

      <DialogContentText style={customColor} id="upload-file14">
        {t("PLS_DATA_COORDINATE_SYSTEM")}
      </DialogContentText>
      <FormControl>
        <RadioGroup
          aria-labelledby="coordinate-system-selection-label"
          name="coordinateSystemOption"
          value={selectedCoordinateOption}
          onChange={(e) =>
            setSelectedCoordinateOption(e.target.value as ECoordinateOptions)
          }
        >
          <FormControlLabel
            value={ECoordinateOptions.DASHBOARD}
            control={<Radio />}
            label={t("ALIGN_SMART_DASHBOARD")}
            disabled={
              jobsiteId &&
              !selectedJobsiteCoordinateSystem &&
              !coordinateSystemLoading
            }
            style={{ marginLeft: 20 }}
          />
          <FormControlLabel
            value={ECoordinateOptions.PUBLIC_COORDINATE}
            control={<Radio />}
            label={t("CHOOSE_COORD_SYSTEM")}
            style={{ marginLeft: 20 }}
          />
          {selectedCoordinateOption ===
            ECoordinateOptions.PUBLIC_COORDINATE && (
            <div className="coordinate-select">
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div
                  style={{
                    color:
                      theme.palette.mode === "dark" ? "#ffffffff" : "#00000099",
                    margin: "0 0 8px",
                    fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                    maxWidth: 126,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {t("COORDINATE_SYSTEM")}
                </div>
                <Autocomplete
                  autoHighlight={true}
                  value={getValueSelect("horizontal")}
                  size="small"
                  options={coordinateSystemData}
                  onChange={(event, data) => {
                    const newLocalizeSelect = selectedDestData.map((item) => {
                      if (item.id === "horizontal") {
                        item = { ...item, ...data };
                      }
                      return item;
                    });
                    setSelectedData(newLocalizeSelect);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      value=""
                      placeholder={t("PLS_SELECT")}
                    />
                  )}
                  sx={{ marginBottom: 1, marginLeft: 5 }}
                  style={{ minWidth: 530, width: "480px" }}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div
                  style={{
                    color:
                      theme.palette.mode === "dark" ? "#ffffffff" : "#00000099",
                    margin: "0 0 8px",
                    fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                    maxWidth: 126,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {t("VERTICAL_COORDINATE_SYSTEM")}
                </div>
                <Autocomplete
                  autoHighlight={true}
                  value={getValueSelect("vertical")}
                  size="small"
                  options={verticalShift}
                  onChange={(event, data) => {
                    const newLocalizeSelect = selectedDestData.map((item) => {
                      if (item.id === "vertical") {
                        item = { ...item, ...data };
                      }
                      return item;
                    });
                    setSelectedData(newLocalizeSelect);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      value=""
                      placeholder={t("PLS_SELECT")}
                    />
                  )}
                  sx={{ marginBottom: 1, marginLeft: 5 }}
                  style={{ minWidth: 530, width: "480px" }}
                />
              </div>
            </div>
          )}
        </RadioGroup>
      </FormControl>

      <DialogActions
        style={{
          marginTop: "10px",
          justifyContent: "center",
          ...customBackgroundStyles,
        }}
      >
        <CustomCanselButton
          size="small"
          variant="contained"
          onClick={handleClose}
          id="upload-file19"
        >
          {t("CANCEL")}
        </CustomCanselButton>
        <LoadingButton
          className={`upload-check-com ${
            uploadCheck || loadingFile ? "upload-check1" : "upload-check2"
          }`}
          size="small"
          variant="contained"
          disabled={uploadCheck || loadingFile}
          loading={whileLoading}
          onClick={uploadProjectFile}
          style={
            uploadCheck || loadingFile
              ? { boxShadow: "none" }
              : { ...customMainColor, boxShadow: "none" }
          }
        >
          {t("UPLOAD")}
        </LoadingButton>
      </DialogActions>

      <ImportFileGroupWare
        openDialog={openImportDialog}
        setOpenDialog={setOpenImportDialog}
        fileInfo={fileInfo}
        setFileData={setFileData}
        setFileInfoSelected={setFileInfoSelected}
        fileInfoFrom={fileInfoFrom}
        loadingFileGroupware={loadingFileGroupware}
      />
    </>
  );
};

export default UploadARFileDialog;

type CustomPopoverProps = {
  anchorOrigin: PopoverOrigin;
  transformOrigin: PopoverOrigin;
  content: string;
  customColor: { [key: string]: string };
};

const CustomPopover = ({
  anchorOrigin,
  transformOrigin,
  content,
  customColor,
}: CustomPopoverProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const replaceNewLine = (s: String) => {
    return s.split("\n").map((line) => (
      <span>
        {line}
        <br />
      </span>
    ));
  };

  const open = Boolean(anchorEl);

  return (
    <div>
      <IconButton onClick={handleClick} style={{ padding: 0 }}>
        <FontAwesomeIcon icon={faCircleQuestion} style={customColor} />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
      >
        <Typography sx={{ p: 2 }} style={{ width: "600px" }}>
          {content ? replaceNewLine(content) : ""}
        </Typography>
      </Popover>
    </div>
  );
};
